/**
 * Desktop-only background services (WP7.8b): reopen the workstation's scanner, then keep the
 * reference cache fresh and replay the offline outbox whenever the app is online. Everything is a
 * no-op in a plain browser tab. Failures never surface as errors here — the sync queue screen shows
 * the real state; a background timer must not toast.
 */
import { isDesktop, restoreScanner } from './desktop.js';
import { syncOutboxNow } from './outbox-queue.js';
import { refreshReferenceCache } from './reference-cache.js';

export const SYNC_INTERVAL_MS = 60_000;
/** The cache is re-pulled far less often than the (cheap, usually empty) outbox replay. */
export const CACHE_REFRESH_INTERVAL_MS = 15 * 60_000;

export interface DesktopServiceDeps {
  isDesktop: () => boolean;
  isOnline: () => boolean;
  restoreScanner: () => Promise<boolean>;
  sync: () => Promise<unknown>;
  refreshCache: () => Promise<unknown>;
  setInterval: (fn: () => void, ms: number) => unknown;
  clearInterval: (handle: unknown) => void;
  addOnlineListener: (fn: () => void) => () => void;
}

function defaultDeps(): DesktopServiceDeps {
  return {
    isDesktop,
    isOnline: () => (typeof navigator === 'undefined' ? true : navigator.onLine),
    restoreScanner: () => restoreScanner(),
    sync: syncOutboxNow,
    refreshCache: refreshReferenceCache,
    setInterval: (fn, ms) => globalThis.setInterval(fn, ms),
    clearInterval: (handle) => globalThis.clearInterval(handle as ReturnType<typeof setInterval>),
    addOnlineListener: (fn) => {
      globalThis.addEventListener('online', fn);
      return () => globalThis.removeEventListener('online', fn);
    },
  };
}

/** Starts the services; returns a stop function. Safe to call in a browser (does nothing). */
export function startDesktopServices(overrides: Partial<DesktopServiceDeps> = {}): () => void {
  const deps = { ...defaultDeps(), ...overrides };
  if (!deps.isDesktop()) return () => {};

  let syncing = false;
  let refreshingCache = false;
  let stopped = false;
  const guarded = async (job: () => Promise<unknown>): Promise<void> => {
    if (stopped || !deps.isOnline()) return;
    try {
      await job();
    } catch {
      // Transient (server down, expired session): retried on the next tick / online event.
    }
  };
  const syncOnce = async (): Promise<void> => {
    if (stopped || syncing) return;
    syncing = true;
    try {
      await guarded(deps.sync);
    } finally {
      syncing = false;
    }
  };
  const refreshCacheOnce = async (): Promise<void> => {
    if (stopped || refreshingCache) return;
    refreshingCache = true;
    try {
      await guarded(deps.refreshCache);
    } finally {
      refreshingCache = false;
    }
  };

  void deps.restoreScanner();
  void syncOnce();
  void refreshCacheOnce();
  const syncTimer = deps.setInterval(() => void syncOnce(), SYNC_INTERVAL_MS);
  const cacheTimer = deps.setInterval(() => void refreshCacheOnce(), CACHE_REFRESH_INTERVAL_MS);
  const removeOnline = deps.addOnlineListener(() => {
    void syncOnce();
    void refreshCacheOnce();
  });

  return () => {
    if (stopped) return;
    stopped = true;
    deps.clearInterval(syncTimer);
    deps.clearInterval(cacheTimer);
    removeOnline();
  };
}
